import moment from 'moment-business-days'
import momenttz from 'moment-timezone'
import {z} from 'zod'

import {FormValidationInput} from '../../../types'
import {MixConfiguration, MixForm} from '../../mix/types'
import {DATE_FORMAT} from '../dateTime.constants'
import {getSelectableDateRange, isShippingDateOutOfLeadTime} from '../dateTime.utils'
import {DateTimeConfiguration, DateTimeForm} from '../types'

import {ErrorCodes} from './errorCodes'

export const validateDateTime = (
  data: DateTimeForm & Pick<MixForm, 'contract'>,
  ctx: z.RefinementCtx,
  {
    configuration: {
      dateTime: dateTimeConfig,
      mix: {contracts}
    },
    defaultValues,
    isOrderChangeProcess
  }: FormValidationInput<DateTimeConfiguration & MixConfiguration>
) => {
  const {dateTime} = data
  // can we use somehow data from useMix Hook?
  const contract = contracts.find((c) => c.contractNumber === data.contract?.contractNumber)
  const {businessDays, businessHours} = dateTimeConfig

  if (!businessDays || !businessHours) return

  const {minDate, maxDate} = getSelectableDateRange(
    contract?.dateRange,
    businessHours,
    businessDays
  )
  const timeZone = businessHours.timeZone

  const toBusinessDate = (value: string) =>
    (timeZone ? momenttz.tz(value, timeZone) : moment(value)).format(DATE_FORMAT)

  // Order change mirrors the backend, which validates date rules only when the delivery date
  // changes; time-only changes and UE/CC adjusted times on the same day must stay submittable.
  const unchangedDates = isOrderChangeProcess
    ? (defaultValues?.dateTime ?? []).map(toBusinessDate)
    : []

  const dateTimeArray = Array.isArray(dateTime) ? dateTime : [dateTime]

  dateTimeArray.forEach((dateTimeValue) => {
    if (!dateTimeValue) return
    if (unchangedDates.includes(toBusinessDate(dateTimeValue))) return

    const fieldPath = Array.isArray(dateTime) ? ['dateTime', dateTimeValue] : ['dateTime']

    if (!moment(dateTimeValue).isBusinessDay()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: fieldPath,
        message: ErrorCodes.notABusinessDay
      })
    }

    if (isShippingDateOutOfLeadTime(moment(dateTimeValue), businessDays, timeZone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: fieldPath,
        message: ErrorCodes.outOfLeadTime
      })
    }

    const isInRange = moment(dateTimeValue).isBetween(minDate, maxDate, 'second', '[]')
    if (!isInRange) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: fieldPath,
        message: ErrorCodes.outOfRange
      })
    }
  })
}
